import { intersect, inCone, corner, CornerType, isCCW, isCW, Axis, comparePoint2D, } from "./geometry.js";
/**
 * checks if 2 points on a polygon have a direct line of sight. ia and ib are
 * the indices of points a and b on the polygon
 */
export const isVisible = (polygon, ia, ib, check = false) => {
    // perform sanity check when requested
    // test if indices provided are in bounds
    // test if indices not adjacent: if so, point a and b do not have a direct
    // line of sight
    let haveLineOfSight = check !== true ||
        (!circularAdjacent(ia, ib, polygon) &&
            Math.min(ia, ib) >= 0 &&
            Math.max(ia, ib) < polygon.length);
    const a = polygon[ia];
    const b = polygon[ib];
    let i0 = polygon.length - 1;
    let i1 = 0;
    while (haveLineOfSight && i1 < polygon.length) {
        if (!(i0 === ia || i0 === ib || i1 === ia || i1 === ib)) {
            // i0, i1, ia and ib are all different
            haveLineOfSight = !intersect(polygon[i0], polygon[i1], a, b);
        }
        i0 = i1;
        i1++;
    }
    return haveLineOfSight;
};
export const isDiagonal = (polygon, ia, ib) => {
    // test if indices not adjacent: if so, point a and b do not have a direct
    // line of sight and therefore no valid diagonal
    let validDiagonal = !circularAdjacent(ia, ib, polygon);
    if (validDiagonal) {
        const iaP = circularPrev(ia, polygon);
        const iaS = circularNext(ia, polygon);
        const ibP = circularPrev(ib, polygon);
        const ibS = circularNext(ib, polygon);
        validDiagonal =
            inCone(polygon[iaP], polygon[ia], polygon[iaS], polygon[ib]) &&
                inCone(polygon[ibP], polygon[ib], polygon[ibS], polygon[ia]) &&
                isVisible(polygon, ia, ib, false);
    }
    return validDiagonal;
};
export const isPolygonCCW = (polygon) => {
    const bri = corner(polygon, CornerType.BOTTOM_RIGHT);
    const ni = (bri + 1) % polygon.length;
    const pi = (bri + polygon.length - 1) % polygon.length;
    return isCCW(polygon[pi], polygon[bri], polygon[ni]);
};
export const getPolygonCCW = (polygon) => {
    return isPolygonCCW(polygon) ? polygon.slice() : polygon.slice().reverse();
};
export const getPolygonCW = (polygon) => {
    return isPolygonCCW(polygon) ? polygon.slice().reverse() : polygon.slice();
};
export const getPolygonArea = (polygon) => {
    return (polygon.reduce((area, nextVertex, index) => {
        area.A2 +=
            (area.vertex[0] + nextVertex[0]) * (nextVertex[1] - area.vertex[1]);
        area.vertex = nextVertex;
        return area;
    }, { A2: 0.0, vertex: polygon[polygon.length - 1] }).A2 / 2);
};
export const getPolygonAverageVertex = (polygon) => {
    const n = polygon.length;
    return polygon.reduce((avg, vertex) => {
        avg[0] += vertex[0] / n;
        avg[1] += vertex[1] / n;
        return avg;
    }, [0, 0]);
};
export const insidePolygon = (polygon, point) => {
    if (polygon == null || polygon.length <= 4) {
        return false;
    }
    let j = polygon.length - 1;
    let inside = false;
    for (let i = 0; i < polygon.length; i++) {
        if ((polygon[i][1] < point[1] && polygon[j][1] >= point[1]) ||
            (polygon[j][1] < point[1] && polygon[i][1] >= point[1])) {
            if (polygon[i][0] +
                ((point[1] - polygon[i][1]) / (polygon[j][1] - polygon[i][1])) *
                    (polygon[j][0] - polygon[i][0]) <
                point[0]) {
                inside = !inside;
            }
        }
        j = i;
    }
    return inside;
};
// for circular lists
export const circularNext = (index, circularList) => (index + circularList.length + 1) % circularList.length;
export const circularPrev = (index, circularList) => (index + circularList.length - 1) % circularList.length;
export const circularAdjacent = (ia, ib, circularList) => Math.abs(ia - ib) <= 1 || Math.abs(ia - ib) == circularList.length - 1;
export class Polygon {
    vertices;
    paths;
    ccw;
    constructor(vertices, paths) {
        this.vertices = vertices;
        this.paths = paths;
        //path[0] is the outer contour and runs CCW , the rest (if any) define the holes and run CW
        this.ccw = this.paths.map((_, pathIndex) => isPolygonCCW(this.getPathVertices(pathIndex)));
    }
    getPathVertices(pathIndex) {
        return this.paths[pathIndex].map((ivrt) => this.vertices[ivrt]);
    }
    area() {
        return this.paths
            .map((_path, pathIndex) => getPolygonArea(this.getPathVertices(pathIndex)))
            .reduce((area, subArea) => area + subArea, 0);
    }
    getAverageVertex() {
        return getPolygonAverageVertex(this.getPathVertices(0));
    }
    toString() {
        return ("[ " +
            this.paths
                .map((path) => "[ " +
                path
                    .map((vertexIndex) => `[ ${this.vertices[vertexIndex][0]}, ${this.vertices[vertexIndex][1]} ]`)
                    .join(", ") +
                " ]")
                .join(",\n") +
            " ]");
        //		let str = '[';
        //		for( let i = 0; i< this.paths[0].length; i++){
        //			str += (i === 0 ? '' : ', ') + `[ ${this.vertices[i][0]}, ${this.vertices[i][1]} ]`;
        //		}
        //		str+=']';
        //		return str;
    }
}
/**
 * This algorithm finds all diagonals that triangulate the given monotone
 * polygon (no holes allowed) in O(n) using the Monotone Triangulation algorithm
 * as described in 'COMPUTATIONAL GEOMETRY IN C' by Joseph O'Rourke.
 *
 * @param polygon
 *            the monotone polygon (CCW) to triangulate.
 * @param axis
 *            The axis which should be considered monotone. Defaults to the y-axis
 * @return the array with all found diagonals and the array with all CCW triangles
 * that make up the poligon. These arrays contain indices who refer to the
 * polygon's indices
 */
const triangulate_monotone = (polygon, axis = Axis.Y) => {
    // setup structures (we call it shackles, referring to the term 'chain') and
    // sort them
    let shackles = polygon.map((vertex, i) => {
        return {
            index: i,
            value: vertex[axis],
            chain: undefined,
        };
    });
    let terminalShackles = shackles.reduce((terminalShackles, shackle) => {
        if (terminalShackles) {
            terminalShackles.start =
                terminalShackles.start.value >= shackle.value
                    ? terminalShackles.start
                    : shackle;
            terminalShackles.end =
                terminalShackles.end.value <= shackle.value
                    ? terminalShackles.end
                    : shackle;
            return terminalShackles;
        }
        else {
            return { start: shackle, end: shackle };
        }
    }, undefined);
    let startVertexIndex = terminalShackles.start.index;
    let chain = 0;
    let vertexIndex = startVertexIndex;
    // determine for each shackle whether they lie on the CCW (chain 0) or the CW
    // (chain 1) side
    // both the start and the end shackle lie on chain 0
    // We start with the start shackle first (so chain 0) until we reach the end
    // shackle. All
    // remaining shackles are automatically on chain 1
    do {
        shackles[vertexIndex].chain = chain;
        vertexIndex = circularNext(vertexIndex, shackles);
        if (vertexIndex === terminalShackles.end.index) {
            chain = 1;
        }
    } while (vertexIndex != startVertexIndex);
    // Now we carefully sort the shackle by their value on the Axis of interest
    // (either Y or X).
    // Special care needs to be taken for vertices that are collinear on the
    // perpendicular Axis
    // (so vertices with same y coordinate when the choosen axis is x and vice
    // versa). When the
    // the vertices lie on different chains then chain 0 goes first. When both
    // vertices lie on
    // chain 0 then the vertex with the lower index goes first. When both
    // vertices lie on chain
    // 1 then the vertex with the higher index goes first. This way we 'follow'
    // the path of the
    // monotone polygon
    shackles.sort((shackle0, shackle1) => {
        const diff = shackle1.value - shackle0.value;
        if (diff) {
            // different value
            return diff;
        }
        else {
            // the equals case
            if (shackle0.chain === shackle1.chain) {
                // same chain
                return shackle0.chain === 1
                    ? shackle1.index - shackle0.index
                    : shackle0.index - shackle1.index;
            }
            else {
                // different chain
                return shackle0.chain - shackle1.chain;
            }
        }
    });
    // When shackle1 is in chain 0 then the triangle should be CW (clock wise) for it to
    // be a valid triangle. When it is on chain 1 it should be CCW (counter clock wise).
    // when it is invalid we choose shackle0 to be the next lower vertex and shackle1 and
    // shackle2 stay as they are. When the triangle is valid but shackle0 is not directly
    // following shackle1 this process is revered. Otherwise: all 3 shackles are the first
    // three shackles of the list of sorted shackles, i.e. [0] is shackle2, [1] is shackle1
    //  and [2] is shackle0
    // when the triangle is valid:
    // When we jump from one chain to the other from shackle0 to shackle1 then we remove
    // shackle1: shackle0 and shackle 2 form the new diagonal.
    // When we stay on the same chain from shackle0 to shackle1 then we remove shackle2:
    // shackle0 and shackle 1 form the new diagonal
    let triangles = [];
    let triangle;
    let diagonals = [];
    let diagonal;
    let thirdIndex = 2;
    let shackle0 = shackles[thirdIndex];
    let shackle1 = shackles[1];
    let shackle2 = shackles[0];
    while (shackles.length > 3) {
        if ((shackle1.chain === 1 &&
            !isCW(polygon[shackle0.index], polygon[shackle1.index], polygon[shackle2.index])) ||
            (shackle1.chain === 0 &&
                !isCCW(polygon[shackle0.index], polygon[shackle1.index], polygon[shackle2.index]))) {
            // a valid triangle/diagonal
            //make sure we return a CCW triangle
            triangle =
                shackle1.chain === 1
                    ? [shackle0.index, shackle1.index, shackle2.index]
                    : [shackle2.index, shackle1.index, shackle0.index];
            //determine diagonal
            diagonal =
                shackle0.chain === shackle1.chain
                    ? [shackle2.index, shackle0.index]
                    : [shackle1.index, shackle0.index];
            triangles.push(triangle);
            diagonals.push(diagonal);
            //remove the correct vertex
            shackles.splice(shackle0.chain === shackle1.chain ? 1 : 0, 1);
            // update the pointer that selects shackle 0 (when necessary: i.e. thirdIndex > 2)
            if (thirdIndex > 2)
                thirdIndex--;
        }
        else {
            // the actual orientation of the three selected vertices does not
            // match the expected orientation
            // skip shackle 0 for now and move on to the next
            thirdIndex++;
        }
        shackle0 = shackles[thirdIndex];
        shackle1 = shackles[1];
        shackle2 = shackles[0];
    }
    //add remaining triangle
    triangle =
        shackle1.chain === 1
            ? [shackle0.index, shackle1.index, shackle2.index]
            : [shackle2.index, shackle1.index, shackle0.index];
    triangles.push(triangle);
    return { triangles: triangles, diagonals: diagonals };
};
export var Side;
(function (Side) {
    Side[Side["LEFT"] = 0] = "LEFT";
    Side[Side["RIGHT"] = 1] = "RIGHT";
})(Side || (Side = {}));
export var Direction;
(function (Direction) {
    Direction[Direction["BOTTOMLEFT"] = 0] = "BOTTOMLEFT";
    Direction[Direction["TOPLEFT"] = 1] = "TOPLEFT";
    Direction[Direction["BOTTOMRIGHT"] = 2] = "BOTTOMRIGHT";
    Direction[Direction["TOPRIGHT"] = 3] = "TOPRIGHT";
})(Direction || (Direction = {}));
export const goingLeft = (direction) => (direction & 2) === 0;
export const goingRight = (direction) => (direction & 2) !== 0;
export const goingDown = (direction) => (direction & 1) === 0;
export const goingUp = (direction) => (direction & 1) !== 0;
export const createSegments = (vertices, axis = Axis.Y, indexOffset = 0, polygonIndex = 0) => {
    let maxVertex;
    let maxSegment;
    return vertices.reduce((accu, vertex, iVrtx, vertices) => {
        const iNext = circularNext(iVrtx, vertices);
        const v0 = vertices[iVrtx];
        const v1 = vertices[iNext];
        const vMin = comparePoint2D(v0, v1, axis) < 0 ? 0 : 1;
        const ovMin = comparePoint2D(v0, v1, 1 - axis) < 0 ? 0 : 1;
        vMin;
        const segment = {
            vertices: [indexOffset + iVrtx, indexOffset + iNext],
            vMin: vMin,
            vMax: 1 - vMin,
            ovMin: vMin,
            ovMax: 1 - vMin,
            self: indexOffset + iVrtx,
            next: indexOffset + iNext,
            prev: indexOffset + circularPrev(iVrtx, vertices),
            polygon: polygonIndex,
            inside: vMin === 0 ? Side.LEFT : Side.RIGHT,
            direction: 1 - vMin + 2 * (1 - ovMin),
            //            offset: indexOffset
        };
        const v0AxisValue = v0[axis];
        if (accu.max === undefined || accu.max < v0AxisValue) {
            accu.max = v0AxisValue;
            accu.maxSegment = indexOffset + iVrtx;
        }
        accu.segments.push(segment);
        return accu;
    }, {
        segments: [],
        max: undefined,
        maxSegment: undefined,
    });
};
export const createPathSegments = (path, vertices, axis = Axis.Y, segmentIndexOffset = 0, polygonIndexOffset = 0, polygonIndex = 0) => {
    let maxVertex;
    let maxSegment;
    return path.reduce((accu, iV0, iPath, path) => {
        const vertex = vertices[iV0];
        const iV1 = path[circularNext(iPath, path)];
        const v0 = vertices[iV0];
        const v1 = vertices[iV1];
        const vMin = comparePoint2D(v0, v1, axis) < 0 ? 0 : 1;
        const ovMin = comparePoint2D(v0, v1, 1 - axis) < 0 ? 0 : 1;
        vMin;
        const segment = {
            vertices: [polygonIndexOffset + iV0, polygonIndexOffset + iV1],
            vMin: vMin,
            vMax: 1 - vMin,
            ovMin: vMin,
            ovMax: 1 - vMin,
            self: segmentIndexOffset + iPath,
            next: segmentIndexOffset + circularNext(iPath, path),
            prev: segmentIndexOffset + circularPrev(iPath, path),
            polygon: polygonIndex,
            inside: vMin === 0 ? Side.LEFT : Side.RIGHT,
            direction: 1 - vMin + 2 * (1 - ovMin),
            //            offset: polygonIndexOffset
        };
        const v0AxisValue = v0[axis];
        if (accu.max === undefined || accu.max < v0AxisValue) {
            accu.max = v0AxisValue;
            accu.maxSegment = segmentIndexOffset + iPath;
        }
        accu.segments.push(segment);
        return accu;
    }, {
        segments: [],
        max: undefined,
        maxSegment: undefined,
    });
};
